import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';

// Поиск клиента по мере ввода — чтобы взять его Telegram ID / Max ID (карточка сотрудника).
// Буквы (можно с «@») — имя, фамилия или username в Telegram/Max; цифры — Telegram/Max ID
// или телефон. Выбор строки отдаёт клиента в onPick.
// Режим «одно поле» (value + onChange): поле само и есть ID — цифры не ищем (это уже ID),
// по буквам подсказываем клиентов с нужным мессенджером (platform), выбор ставит ID в поле.

const clientName = (u) => {
  const parts = [u.first_name || u.tg_first_name || u.max_first_name || u.web_first_name,
    u.last_name || u.tg_last_name || u.max_last_name].filter(Boolean);
  return parts.join(' ') || 'Без имени';
};

const usernames = (u) => [...new Set([u.tg_username, u.max_username, u.username].filter(Boolean))].map(x => `@${x}`);

export default function ClientMessengerSearch({ onPick, value, onChange, platform, placeholder, searchDigits = true }) {
  const controlled = value !== undefined;
  const [ownQuery, setOwnQuery] = useState('');
  const query = controlled ? (value || '') : ownQuery;
  const setQuery = controlled ? (v) => onChange(v) : setOwnQuery;
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    const q = query.trim().replace(/^@/, '');
    const digitsOnly = /^\d+$/.test(q);
    if (q.length < 2 || (digitsOnly && !searchDigits)) { setResults([]); setLoading(false); return undefined; }
    const id = ++reqId.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        let users;
        if (/^\+?[\d\s()-]+$/.test(q)) {
          const digits = q.replace(/\D/g, '');
          const [byId, byPhone] = await Promise.all([
            api.getUsers({ searchPlatformId: digits, limit: 8 }),
            digits.length >= 6 ? api.getUsers({ searchPhone: digits, limit: 8 }) : Promise.resolve({ users: [] }),
          ]);
          const seen = new Set();
          users = [...(byId.users || []), ...(byPhone.users || [])].filter(u => !seen.has(u.id) && seen.add(u.id));
        } else {
          users = (await api.getUsers({ searchUsername: q, limit: 8 })).users || [];
        }
        // В мессенджер писать можно только тем, у кого есть Telegram или Max (или нужный — platform)
        const usable = (u) => (platform === 'telegram' ? u.telegram_id : platform === 'max' ? u.max_id : (u.telegram_id || u.max_id));
        if (id === reqId.current) setResults(users.filter(usable).slice(0, 8));
      } catch {
        if (id === reqId.current) setResults([]);
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, platform, searchDigits]);

  const q = query.trim().replace(/^@/, '');
  const open = focused && q.length >= 2 && !(!searchDigits && /^\d+$/.test(q));

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-input"
        placeholder={placeholder || 'Имя, @username, телефон или ID клиента'}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {open && (
        <div style={{
          position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 4, zIndex: 20,
          background: '#fff', border: '1px solid #D8DDDA', '--ch': '6px', '--stroke': '#D8DDDA',
          boxShadow: '0 6px 18px rgba(28,61,46,0.12)', maxHeight: 320, overflowY: 'auto',
        }}>
          {loading && results.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 13, color: '#909894' }}>Ищем…</div>
          )}
          {!loading && results.length === 0 && (
            <div style={{ padding: '10px 12px', fontSize: 13, color: '#909894' }}>
              Не нашли. Пусть сотрудник откроет бота магазина и нажмёт «Старт» — тогда он появится в клиентах
            </div>
          )}
          {results.map(u => (
            // onMouseDown — иначе blur поля закрывает список раньше клика
            <div key={u.id}
              onMouseDown={(e) => { e.preventDefault(); onPick(u); if (!controlled) setQuery(''); setResults([]); }}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #EBEDEC' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#F4F4EF'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: '#1C3D2E' }}>
                {clientName(u)}
                {usernames(u).length > 0 && (
                  <span style={{ fontWeight: 400, color: '#5E6561', marginLeft: 6 }}>{usernames(u).join(' · ')}</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#909894', marginTop: 2 }}>
                {[u.telegram_id && `Telegram ${u.telegram_id}`, u.max_id && `Max ${u.max_id}`, u.phone].filter(Boolean).join(' · ')}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
